import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LoadingIndicator } from '../../Tools/LoadingIndicator';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { InstagramLoginButton, authenticateInstagram } from '../../Buttons/Facebook/InstagramLoginButton';
import useThemeColors from '../../../hooks/useThemeColors';

// Import reusable components
import ChartCard from '../ChartCard';
import PlatformMetricCard from '../PlatformMetricCard';
import { SkeletonMetricCard, SkeletonTable, SkeletonChart, SkeletonHashtagGrid, SkeletonDemographics } from '../AnalyticsSkeletons';

// Import React Icons
import { 
  FiUsers, 
  FiEye, 
  FiHeart, 
  FiBarChart2, 
  FiPlay,
  FiAlertCircle,
  FiRefreshCw,
  FiImage,
  FiVideo,
  FiInstagram,
  FiBookmark,
  FiHash,
  FiTarget,
  FiUserPlus
} from 'react-icons/fi';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const InstagramAnalytics = () => {
  const { activeWorkspace, idToken } = useWorkspace();
  const navigate = useNavigate();
  const { isDark } = useThemeColors();

  // Instagram Analytics State
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [selectedInstagramAccount, setSelectedInstagramAccount] = useState(null);
  const [instagramAnalytics, setInstagramAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  // API Functions
  const fetchInstagramAccounts = useCallback(async () => {
    if (!activeWorkspace || !idToken) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/instagram/accounts`,
        {
          params: { workspaceId: activeWorkspace.id },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setInstagramAccounts(response.data.accounts || []);
      
      // Auto-select first account if available
      if (response.data.accounts && response.data.accounts.length > 0) {
        setSelectedInstagramAccount(response.data.accounts[0].platform_id);
      }
    } catch (error) {
      console.error('Error fetching Instagram accounts:', error);
      setAnalyticsError('Failed to load Instagram accounts. Please connect an Instagram account first.');
    }
  }, [activeWorkspace, idToken]);

  const fetchInstagramAnalytics = useCallback(async (platformId, days = 30) => {
    if (!activeWorkspace || !idToken || !platformId) return;
    
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/instagram/analytics`,
        {
          params: { 
            workspaceId: activeWorkspace.id, 
            platform_id: platformId,
            days: days
          },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setInstagramAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching Instagram analytics:', error);
      if (error.response?.status === 401) {
        setAnalyticsError('Instagram token expired. Please reconnect your Instagram account.');
      } else if (error.response?.status === 400) {
        setAnalyticsError('Please make sure your Instagram account is a Business or Creator account.');
      } else {
        setAnalyticsError('Failed to load Instagram analytics. Please try again.');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeWorkspace, idToken]);

  // Fetch Instagram accounts when component mounts
  useEffect(() => {
    if (activeWorkspace && idToken) {
      fetchInstagramAccounts();
    }
  }, [fetchInstagramAccounts, activeWorkspace, idToken]);

  // Fetch Instagram analytics when account or days selection changes
  useEffect(() => {
    if (selectedInstagramAccount) {
      fetchInstagramAnalytics(selectedInstagramAccount, selectedDays);
    }
  }, [selectedInstagramAccount, selectedDays, fetchInstagramAnalytics]);

  // Filter posts based on selected time period
  const getFilteredPosts = () => {
    if (!instagramAnalytics?.posts) return [];
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (selectedDays * 24 * 60 * 60 * 1000));
    
    return instagramAnalytics.posts.filter(post => {
      const postDate = new Date(post.timestamp);
      return postDate >= cutoffDate;
    });
  };

  const filteredPosts = getFilteredPosts();

  return (
    <>
      {/* Instagram Account Selection */}
      {instagramAccounts.length > 1 && (
        <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4 --theme-colors">Select Instagram Account</h3>
          <div className="flex flex-wrap gap-3">
            {instagramAccounts.map((account) => (
              <button
                key={account.platform_id}
                onClick={() => setSelectedInstagramAccount(account.platform_id)}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg border transition-colors ${
                  selectedInstagramAccount === account.platform_id
                    ? isDark
                      ? 'border-purple-400 bg-purple-900/30 text-purple-300'
                      : 'border-purple-500 bg-purple-50 text-purple-700'
                    : isDark
                      ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {account.logo && (
                  <img 
                    src={account.logo} 
                    alt={account.username}
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                  />
                )}
                <span className="font-medium text-sm sm:text-base">@{account.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {analyticsError && (
        <div className={`rounded-lg p-4 sm:p-6 border ${
          isDark
            ? 'bg-red-900/30 border-red-700'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <FiAlertCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <p className={isDark ? 'text-red-300' : 'text-red-800'}>{analyticsError}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <button
              onClick={() => fetchInstagramAccounts()}
              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'text-red-400 hover:text-red-300 border-red-700 hover:bg-red-900/20'
                  : 'text-red-600 hover:text-red-700 border-red-300 hover:bg-red-100'
              }`}
            >
              <FiRefreshCw className="w-4 h-4" />
              Retry
            </button>
            {analyticsError.includes('connect') && (
              <div className="w-full sm:w-auto sm:max-w-xs">
                <InstagramLoginButton onClick={() => authenticateInstagram(idToken)} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Content */}
      {(selectedInstagramAccount || analyticsLoading) && !analyticsError && (
        <>
          {/* Account Display & Period Selection */}
          {!analyticsLoading && instagramAnalytics && (
            <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Account Info - Left Side */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600'
                      : 'bg-gradient-to-r from-purple-400 to-pink-400'
                  }`}>
                    <FiInstagram className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold --theme-colors">
                      {instagramAccounts.find(acc => acc.platform_id === selectedInstagramAccount)?.username || 'Instagram Account'}
                    </h3>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Analytics for @{instagramAccounts.find(acc => acc.platform_id === selectedInstagramAccount)?.username || 'account'} • Last {selectedDays} days
                    </p>
                  </div>
                </div>
                
                {/* Period Selection - Right Side */}
                <div className="flex flex-wrap gap-2">
                  {[7, 30, 60, 90].map((days) => (
                    <button
                      key={days}
                      onClick={() => setSelectedDays(days)}
                      className={`px-3 sm:px-4 py-2 rounded-lg border transition-colors text-xs sm:text-sm ${
                        selectedDays === days
                          ? isDark
                            ? 'border-purple-400 bg-purple-900/30 text-purple-300'
                            : 'border-purple-500 bg-purple-50 text-purple-700'
                          : isDark
                            ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Last {days} days
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* All Metrics in Single Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {analyticsLoading ? (
              <>
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
              </>
            ) : instagramAnalytics ? (
              <>
                <PlatformMetricCard
                  title="Followers"
                  value={instagramAnalytics.overview.total_followers?.toLocaleString() || '0'}
                  icon={FiUsers}
                  platformColor="purple"
                  subtitle="Total"
                />
                <PlatformMetricCard
                  title="Avg Reach"
                  value={instagramAnalytics.overview.avg_reach?.toLocaleString() || '0'}
                  icon={FiEye}
                  platformColor="purple"
                  subtitle="Per post"
                />
                <PlatformMetricCard
                  title="Avg Impressions"
                  value={instagramAnalytics.overview.avg_impressions?.toLocaleString() || '0'}
                  icon={FiBarChart2}
                  platformColor="purple"
                  subtitle="Per post"
                />
                <PlatformMetricCard
                  title="Total Posts"
                  value={instagramAnalytics.overview.total_media?.toLocaleString() || '0'}
                  icon={FiImage}
                  platformColor="blue"
                  subtitle="Published"
                />
              </>
            ) : null}
          </div>

          {/* Recent Posts Table */}
          {(analyticsLoading || instagramAnalytics) && (
            <ChartCard
              title="Recent Posts Performance"
              description={analyticsLoading ? "Loading posts..." : `Showing ${filteredPosts?.length || 0} posts from the last ${selectedDays} days`}
              icon={FiBarChart2}
            >
              {analyticsLoading ? (
                <SkeletonTable />
              ) : filteredPosts && filteredPosts.length > 0 ? (
               <div className="space-y-4">
                 {/* Posts Summary Metrics */}
                 <div className={`rounded-lg p-4 ${
                   isDark 
                     ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30' 
                     : 'bg-gradient-to-r from-blue-50 to-purple-50'
                 }`}>
                   <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                     <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                       <div className="flex items-center gap-3">
                         <FiHeart className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                         <div>
                           <p className={`text-xs sm:text-sm font-medium ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Overall Engagement</p>
                           <p className={`text-lg sm:text-xl font-bold ${isDark ? 'text-purple-200' : 'text-purple-900'}`}>
                             {instagramAnalytics.overview.engagement_rate ? `${instagramAnalytics.overview.engagement_rate}%` : '0%'}
                           </p>
                         </div>
                       </div>
                       <div className="flex items-center gap-3">
                         <FiBookmark className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                         <div>
                           <p className={`text-xs sm:text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Total Saved</p>
                           <p className={`text-lg sm:text-xl font-bold ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>
                             {instagramAnalytics.overview.total_saved?.toLocaleString() || '0'}
                           </p>
                         </div>
                       </div>
                     </div>
                    <div className="text-left sm:text-right">
                      <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Posts Analyzed</p>
                      <p className={`text-base sm:text-lg font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {filteredPosts.length}
                      </p>
                    </div>
                   </div>
                 </div>
                 
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-full">
                    <thead>
                      <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <th className={`text-left py-3 px-2 font-medium min-w-[200px] lg:min-w-[250px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Post</th>
                        <th className={`text-left py-3 px-2 font-medium min-w-[60px] lg:min-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Type</th>
                        <th className={`text-left py-3 px-2 font-medium min-w-[60px] lg:min-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Date</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[50px] lg:min-w-[60px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Likes</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[70px] lg:min-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Comments</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[50px] lg:min-w-[60px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Reach</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[70px] lg:min-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Impressions</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[50px] lg:min-w-[60px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Saved</th>
                        <th className={`text-right py-3 px-2 font-medium min-w-[70px] lg:min-w-[80px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Engagement</th>
                      </tr>
                    </thead>
                <tbody>
                  {filteredPosts.map((post, index) => (
                    <tr key={post.id} className={`border-b ${isDark ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                       <td className="py-3 px-2">
                         <div className="flex items-start gap-3">
                           {post.thumbnail_url && (
                             <img 
                               src={post.thumbnail_url} 
                               alt="Post thumbnail"
                               className="w-10 h-10 rounded object-cover flex-shrink-0"
                             />
                           )}
                           <div className="min-w-0 flex-1">
                             <p className={`text-sm font-medium line-clamp-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                               {post.caption || 'No caption'}
                             </p>
                             {post.hashtags && post.hashtags.length > 0 && (
                               <div className="flex flex-wrap gap-1 mt-2">
                                 {post.hashtags.map((hashtag, i) => (
                                   <span 
                                     key={i} 
                                     className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                                       isDark 
                                         ? 'text-purple-300 bg-purple-900/40' 
                                         : 'text-purple-600 bg-purple-100'
                                     }`}
                                     title={hashtag}
                                   >
                                     {hashtag}
                                   </span>
                                 ))}
                               </div>
                             )}
                           </div>
                         </div>
                       </td>
                       <td className="py-3 px-2">
                         <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                           post.media_type === 'VIDEO' 
                             ? isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'
                             : post.media_type === 'CAROUSEL_ALBUM' 
                               ? isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                               : isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'
                         }`}>
                           {post.media_type === 'VIDEO' ? <FiVideo className="w-3 h-3" /> : <FiImage className="w-3 h-3" />}
                           <span className="hidden sm:inline">{post.media_type}</span>
                           <span className="sm:hidden">
                             {post.media_type === 'VIDEO' ? 'VID' : 
                              post.media_type === 'CAROUSEL_ALBUM' ? 'CAR' : 'IMG'}
                           </span>
                         </span>
                       </td>
                       <td className={`py-3 px-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                         <span className="hidden sm:block">
                           {new Date(post.timestamp).toLocaleDateString()}
                         </span>
                         <span className="sm:hidden">
                           {new Date(post.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                         </span>
                       </td>
                       <td className={`py-3 px-2 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                         {post.has_insights ? (post.likes?.toLocaleString() || '0') : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>
                         )}
                       </td>
                       <td className={`py-3 px-2 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                         {post.has_insights ? (post.comments?.toLocaleString() || '0') : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>
                         )}
                       </td>
                       <td className={`py-3 px-2 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                         {post.has_insights ? (post.reach?.toLocaleString() || '0') : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>
                         )}
                       </td>
                       <td className={`py-3 px-2 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                         {post.has_insights ? (post.impressions?.toLocaleString() || '0') : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>
                         )}
                       </td>
                       <td className={`py-3 px-2 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                         {post.has_insights ? (post.saved?.toLocaleString() || '0') : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No data</span>
                         )}
                       </td>
                       <td className="py-3 px-2 text-right">
                         {post.has_insights ? (
                           <span className={`text-sm font-medium ${
                             post.engagement_rate && parseFloat(post.engagement_rate) > 3 
                               ? isDark ? 'text-green-400' : 'text-green-600'
                               : isDark ? 'text-gray-400' : 'text-gray-600'
                           }`}>
                             {post.engagement_rate ? `${post.engagement_rate}%` : 'N/A'}
                           </span>
                         ) : (
                           <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`} title={post.insights_note}>
                             No insights
                           </span>
                         )}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
              </div>
              ) : (
                <div className="text-center py-12">
                  <FiImage className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                  <h3 className={`text-base sm:text-lg font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No posts in the last {selectedDays} days</h3>
                  <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Try selecting a longer time period or publish some content to see your performance metrics.
                  </p>
                </div>
              )}
            </ChartCard>
          )}

          {/* Daily Insights Charts */}
          {(analyticsLoading || instagramAnalytics?.insights) && (
            <div className="space-y-6">
              {/* Daily Reach - Full Width */}
              <ChartCard
                 title="Daily Reach"
                 description={analyticsLoading ? "Loading insights..." : "Your daily reach over the selected period"}
                 icon={FiEye}
               >
                {analyticsLoading ? (
                  <SkeletonChart />
                ) : (
                <div className={`h-64 rounded-lg flex items-center justify-center ${
                  isDark 
                    ? 'bg-gradient-to-r from-purple-900/30 to-purple-800/30' 
                    : 'bg-gradient-to-r from-purple-50 to-purple-100'
                }`}>
                  <div className="text-center">
                    <FiBarChart2 className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-purple-400' : 'text-purple-400'}`} />
                    <p className={`font-medium ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>Daily Reach Chart</p>
                    <p className={`text-sm mt-1 ${isDark ? 'text-purple-400' : 'text-purple-500'}`}>
                      {instagramAnalytics.insights.daily_reach?.length || 0} data points
                    </p>
                  </div>
                </div>
                )}
              </ChartCard>

              {/* Follows and Unfollows - Full Width */}
              {(analyticsLoading || (instagramAnalytics?.insights?.daily_follows_unfollows && instagramAnalytics.insights.daily_follows_unfollows.length > 0)) && (
                <ChartCard
                  title="Daily Follows & Unfollows"
                  description={analyticsLoading ? "Loading follower data..." : "Track your daily follower growth and losses"}
                  icon={FiUserPlus}
                >
                  {analyticsLoading ? (
                    <SkeletonChart />
                  ) : (
                  <div className={`h-64 rounded-lg flex items-center justify-center ${
                    isDark 
                      ? 'bg-gradient-to-r from-green-900/30 to-blue-900/30' 
                      : 'bg-gradient-to-r from-green-50 to-blue-50'
                  }`}>
                    <div className="text-center">
                      <FiUserPlus className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-green-400' : 'text-green-500'}`} />
                      <p className={`font-medium ${isDark ? 'text-green-300' : 'text-green-600'}`}>Follows & Unfollows Chart</p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-green-400' : 'text-green-500'}`}>
                        {instagramAnalytics.insights.daily_follows_unfollows?.length || 0} data points
                      </p>
                      <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 mt-4">
                        <div className="text-center">
                          <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                            {instagramAnalytics.insights.daily_follows_unfollows.reduce((sum, day) => sum + day.follows, 0)}
                          </div>
                          <div className={`text-xs sm:text-sm ${isDark ? 'text-green-300' : 'text-green-500'}`}>Total Follows</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                            {instagramAnalytics.insights.daily_follows_unfollows.reduce((sum, day) => sum + day.unfollows, 0)}
                          </div>
                          <div className={`text-xs sm:text-sm ${isDark ? 'text-red-300' : 'text-red-500'}`}>Total Unfollows</div>
                        </div>
                        <div className="text-center">
                          <div className={`text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            {instagramAnalytics.insights.daily_follows_unfollows.reduce((sum, day) => sum + day.net_growth, 0)}
                          </div>
                          <div className={`text-xs sm:text-sm ${isDark ? 'text-blue-300' : 'text-blue-500'}`}>Net Growth</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </ChartCard>
              )}

            </div>
          )}

          {/* Hashtag Analytics */}
          {(analyticsLoading || (instagramAnalytics?.hashtags && instagramAnalytics.hashtags.length > 0)) && (
            <ChartCard
              title="Hashtag Analytics"
              description={analyticsLoading ? "Loading hashtag data..." : "Most used hashtags in your recent posts"}
              icon={FiHash}
            >
              {analyticsLoading ? (
                <SkeletonHashtagGrid />
              ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {instagramAnalytics.hashtags.slice(0, 12).map((hashtag, index) => (
                  <div key={index} className={`rounded-lg p-4 text-center ${
                    isDark ? 'bg-purple-900/30' : 'bg-purple-50'
                  }`}>
                    <div className={`text-base sm:text-lg font-bold ${isDark ? 'text-purple-300' : 'text-purple-600'}`}>
                      {hashtag.hashtag}
                    </div>
                    <div className={`text-xs sm:text-sm mt-1 ${isDark ? 'text-purple-400' : 'text-purple-500'}`}>
                      Used {hashtag.usage_count} times
                    </div>
                  </div>
                ))}
              </div>
              )}
            </ChartCard>
          )}

          {/* Audience Demographics */}
          {(analyticsLoading || instagramAnalytics?.audience?.gender_age || instagramAnalytics?.audience?.locale) && (
            <ChartCard
              title="Audience Demographics"
              description={analyticsLoading ? "Loading demographics..." : "Your audience breakdown by demographics"}
              icon={FiUsers}
            >
              {analyticsLoading ? (
                <SkeletonDemographics />
              ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {instagramAnalytics.audience.gender_age && (
                  <div>
                    <h4 className={`text-sm sm:text-base font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Follower Demographics</h4>
                    <div className="space-y-2">
                      {Object.entries(instagramAnalytics.audience.gender_age).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{key}</span>
                          <div className="flex items-center gap-2">
                            <div className={`w-20 sm:w-24 h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                              <div 
                                className={`h-full rounded-full ${isDark ? 'bg-purple-500' : 'bg-purple-500'}`}
                                style={{ width: `${typeof value === 'number' ? (value * 100) : parseFloat(value) || 0}%` }}
                              />
                            </div>
                            <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                              {typeof value === 'number' ? (value * 100).toFixed(1) : parseFloat(value)?.toFixed(1) || '0'}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {instagramAnalytics.audience.locale && (
                  <div>
                    <h4 className={`text-sm sm:text-base font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Reached Audience Demographics</h4>
                    <div className="space-y-2">
                      {Object.entries(instagramAnalytics.audience.locale)
                        .sort(([,a], [,b]) => {
                          const aVal = typeof a === 'number' ? a : parseFloat(a) || 0;
                          const bVal = typeof b === 'number' ? b : parseFloat(b) || 0;
                          return bVal - aVal;
                        })
                        .slice(0, 5)
                        .map(([key, value]) => {
                          const numValue = typeof value === 'number' ? value : parseFloat(value) || 0;
                          return (
                            <div key={key} className="flex justify-between items-center">
                              <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{key}</span>
                              <div className="flex items-center gap-2">
                                <div className={`w-20 sm:w-24 h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <div 
                                    className={`h-full rounded-full ${isDark ? 'bg-blue-500' : 'bg-blue-500'}`}
                                    style={{ width: `${(numValue * 100)}%` }}
                                  />
                                </div>
                                <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{(numValue * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
              )}
            </ChartCard>
          )}
        </>
      )}

      {/* No Data State */}
      {!instagramAnalytics && !analyticsLoading && !analyticsError && instagramAccounts.length === 0 && (
        <div className="text-center py-12">
          <FiTarget className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`text-base sm:text-lg font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>No Instagram Account Connected</h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Connect your Instagram Business or Creator account to view analytics.
          </p>
          <button
            onClick={() => navigate('/socials')}
            className={`px-6 py-2 rounded-lg transition-colors ${
              isDark 
                ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            Connect Instagram Account
          </button>
        </div>
      )}
    </>
  );
};

export default InstagramAnalytics;